/**
 * EditContestModal Component
 * Modal for editing remix contests
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditContestModal = ({ contest, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: contest.title || '',
    description: contest.description || '',
    genres: (contest.genres || []).join(', '),
    deadline: contest.deadline ? contest.deadline.split('T')[0] : '',
    prize: contest.prize || '',
    rules: contest.rules || ''
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Contest title is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const genresArray = formData.genres.split(',').map(g => g.trim()).filter(g => g);
      
      const response = await fetch(`${BACKEND_URL}/api/remix-contests/${contest.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          genres: genresArray,
          deadline: formData.deadline || null,
          prize: formData.prize,
          rules: formData.rules
        })
      });

      if (response.ok) {
        onSuccess && onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update contest');
      }
    } catch (error) {
      console.error('Error updating contest:', error);
      alert('Failed to update contest');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this contest? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/remix-contests/${contest.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        onSuccess && onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete contest');
      }
    } catch (error) {
      console.error('Error deleting contest:', error);
      alert('Failed to delete contest');
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.6rem',
    borderRadius: '8px',
    border: '1px solid rgba(167, 139, 250, 0.3)',
    background: 'rgba(30, 20, 50, 0.8)',
    color: '#fff',
    fontSize: '0.85rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    color: '#a78bfa',
    fontSize: '0.85rem',
    marginBottom: '0.4rem'
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
      padding: '1rem'
    }}>
      <div 
        data-testid="edit-contest-modal"
        style={{
          background: 'linear-gradient(135deg, #1a1025 0%, #2d1f3d 50%, #1a1025 100%)',
          borderRadius: '16px',
          border: '2px solid rgba(167, 139, 250, 0.4)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: '1.5rem'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ color: '#a78bfa', fontSize: '1.2rem', margin: 0 }}>
            ✏️ Edit Contest
          </h3>
          <button 
            onClick={onClose} 
            data-testid="close-edit-contest-modal"
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Title */}
          <div>
            <label style={labelStyle}>Contest Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="contest-title-input"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              data-testid="contest-description-input"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Deadline */}
          <div>
            <label style={labelStyle}>Deadline</label>
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              data-testid="contest-deadline-input"
              style={inputStyle}
            />
          </div>

          {/* Prize */}
          <div>
            <label style={labelStyle}>Prize</label>
            <input
              type="text"
              value={formData.prize}
              onChange={(e) => setFormData({ ...formData, prize: e.target.value })}
              placeholder="e.g., Official release, $500"
              data-testid="contest-prize-input"
              style={inputStyle}
            />
          </div>

          {/* Genres */}
          <div>
            <label style={labelStyle}>Genres (comma separated)</label>
            <input
              type="text"
              value={formData.genres}
              onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
              data-testid="contest-genres-input"
              style={inputStyle}
            />
          </div>

          {/* Rules */}
          <div>
            <label style={labelStyle}>Rules</label>
            <textarea
              value={formData.rules}
              onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
              rows={2}
              data-testid="contest-rules-input"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-contest-btn"
              style={{
                padding: '0.65rem 1rem',
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: deleting ? 'not-allowed' : 'pointer'
              }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="save-contest-btn"
              style={{
                flex: 1,
                padding: '0.65rem',
                borderRadius: '8px',
                border: 'none',
                background: loading ? 'rgba(167, 139, 250, 0.3)' : 'linear-gradient(135deg, #a78bfa, #9333ea)',
                color: loading ? 'rgba(255,255,255,0.5)' : '#fff',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditContestModal;
